import React from "react";
import {
  Audio,
  getStaticFiles,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import reelData from "../public/reel-data.json";
import { AudioVisualizer } from "./components/AudioVisualizer";
import {
  compositionIdToTemplateName,
  fallbackTemplate,
  templateRegistryMap,
  type TemplateName,
} from "./registry";
import { ReelMetadata } from "./types";

const data = reelData as ReelMetadata;
type GenericComponent = React.ComponentType<Record<string, unknown>>;

interface MyCompositionProps {
  templateOverride?: TemplateName;
}

export const MyComposition: React.FC<MyCompositionProps> = ({
  templateOverride,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const currentMs = (frame / fps) * 1000;

  const introFrames = Math.ceil((data.introDurationMs / 1000) * fps);
  const outroFrames = Math.ceil((data.outroDurationMs / 1000) * fps);
  const bulletsFrames = durationInFrames - introFrames - outroFrames;
  const clampedProgress = Math.min(
    Math.max((frame / durationInFrames) * 100, 0),
    100,
  );
  const isOutroActive =
    currentMs >= data.audioDurationSeconds * 1000 - data.outroDurationMs;
  const isAudioActive = currentMs <= data.audioDurationSeconds * 1000;

  const subtitleOffsetMs = data.subtitleOffsetMs ?? 150;
  const adjustedSubtitles = React.useMemo(() => {
    return data.subtitles.map((sub) => ({
      ...sub,
      startMs: Math.max(0, sub.startMs - subtitleOffsetMs),
      endMs: Math.max(0, sub.endMs - subtitleOffsetMs),
    }));
  }, [data.subtitles, subtitleOffsetMs]);

  const isWordActive = adjustedSubtitles.some(
    (sub: ReelMetadata["subtitles"][number]) =>
      currentMs >= sub.startMs && currentMs <= sub.endMs,
  );

  const rawTemplate =
    templateOverride ??
    data.template ??
    compositionIdToTemplateName[data.templateId ?? ""] ??
    "zen-minimal";
  const activeTemplate = templateRegistryMap[rawTemplate] ?? fallbackTemplate;

  const headerScale = spring({
    frame: Math.max(0, frame - introFrames),
    fps,
    config: { damping: 15, mass: 0.6, stiffness: 100 },
  });

  const headerTranslateY = spring({
    frame: Math.max(0, frame - introFrames),
    fps,
    from: -50,
    to: 0,
    config: { damping: 15, mass: 0.7 },
  });

  const headerOpacity = interpolate(
    Math.max(0, frame - introFrames),
    [0, 8],
    [0, 1],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const BackgroundComponent =
    activeTemplate.BackgroundComponent as GenericComponent;
  const TemplateComponent =
    activeTemplate.TemplateComponent as GenericComponent;
  const CaptionsComponent =
    activeTemplate.CaptionsComponent as GenericComponent;
  const footerColor =
    activeTemplate.footerTextColor ?? "rgba(255,255,255,0.75)";
  const watermark = data.watermark;

  return (
    <div className="reel-canvas">
      <BackgroundComponent
        {...(activeTemplate.templateName === "spatial-three"
          ? { isWordActive }
          : {})}
      />

      {activeTemplate.progressBarStyle?.display !== "none" && (
        <div className="progress-bar-container">
          <div
            className="progress-bar-fill"
            style={{
              width: `${clampedProgress}%`,
              ...activeTemplate.progressBarStyle,
            }}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          zIndex: 5,
        }}
      >
        <TemplateComponent
          data={data}
          frame={frame}
          fps={fps}
          introFrames={introFrames}
          bulletsFrames={bulletsFrames}
          outroFrames={outroFrames}
          headerOpacity={headerOpacity}
          headerScale={headerScale}
          headerTranslateY={headerTranslateY}
        />

        <div
          style={{
            height: "160px",
            width: "100%",
            margin:
              activeTemplate.templateName === "ink-porcelain"
                ? "40px 0 0px 0"
                : "20px 0 10px 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {frame < durationInFrames - outroFrames ? (
            <CaptionsComponent subtitles={adjustedSubtitles} />
          ) : null}
        </div>

        {!activeTemplate.hideVisualizer && (
          <div
            style={{
              height: "100px",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AudioVisualizer
              active={isWordActive && isAudioActive && !isOutroActive}
              styleConfig={activeTemplate.visualizerStyle}
            />
          </div>
        )}

        <Audio src={staticFile("voice.wav")} volume={1.2} />
        {getStaticFiles().some(
          (file) => file.name === "background_music.mp3",
        ) ? (
          <Audio src={staticFile("background_music.mp3")} volume={0.04} />
        ) : null}
      </div>

      {!activeTemplate.hideWatermark && watermark && frame >= introFrames && (
        <div
          style={{
            position: "absolute",
            bottom:
              activeTemplate.templateName === "svg-hero" ? "100px" : "290px",
            left: "70px",
            right: "70px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              width: "100%",
              height: "2px",
              background: activeTemplate.dividerGradient,
              marginBottom: "16px",
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontFamily:
                activeTemplate.footerFontFamily ?? "var(--font-title)",
              fontSize: "20px",
              fontWeight: 800,
              color: footerColor,
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                color: activeTemplate.instagramIconColor,
                filter:
                  activeTemplate.templateName === "retro-terminal"
                    ? "none"
                    : "drop-shadow(0 0 8px rgba(255,0,127,0.6))",
              }}
            >
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>

            {activeTemplate.watermarkTextColor ? (
              <span style={{ color: activeTemplate.watermarkTextColor }}>
                {watermark}
              </span>
            ) : (
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #ffffff 40%, var(--accent-pink) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {watermark}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
